import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Loader2, Trophy, TrendingUp, Sparkles } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, Tooltip } from "recharts";

const fmt = (n) => (Number(n) || 0).toLocaleString();

/**
 * Compact per-agent audit card. Displays when a single agent is selected on
 * the Ops Dashboard. On the dedicated /ops/agents/{id} page, use it with
 * `variant="full"` for larger metrics.
 *
 * Props:
 *   - agentId (required)
 *   - filters: { brand_id, date_from, date_to, team_id, campaign_id }
 *   - variant: "compact" | "full"
 */
export default function AgentAuditCard({ agentId, filters, variant = "compact" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!agentId) return;
    let cancelled = false;
    (async () => {
      setLoading(true); setErr(""); setData(null);
      try {
        const r = await api.get("/ops/analytics/agent-audit", {
          params: { agent_id: agentId, ...(filters || {}) },
        });
        if (!cancelled) setData(r.data);
      } catch (ex) {
        if (!cancelled) setErr(ex?.response?.data?.detail || "Failed to load agent audit.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [agentId, filters?.brand_id, filters?.date_from, filters?.date_to,
      filters?.team_id, filters?.campaign_id]);

  const sparkData = useMemo(
    () => (data?.per_day || []).map((d) => ({
      date: d.date.slice(5), // MM-DD label
      converted: d.converted,
      calls: d.calls,
    })),
    [data]
  );

  if (!agentId) return null;
  if (loading) {
    return (
      <div className="glass-card p-4 flex items-center gap-2 text-slate-500 text-sm"
           data-testid="agent-audit-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading agent audit…
      </div>
    );
  }
  if (err) {
    return <div className="glass-card p-4 text-sm text-red-600" data-testid="agent-audit-err">{err}</div>;
  }
  if (!data?.found) {
    return (
      <div className="glass-card p-4 text-sm text-slate-500" data-testid="agent-audit-empty">
        {data?.message || "No calls for this agent in the selected scope."}
      </div>
    );
  }

  const t = data.totals;
  const isFull = variant === "full";

  return (
    <div className={`glass-card ${isFull ? "p-6 space-y-5" : "p-4 space-y-3"}`}
         data-testid="agent-audit-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wider text-slate-500">Agent audit</div>
          <div className={`font-outfit font-semibold text-slate-900 ${isFull ? "text-2xl" : "text-lg"} truncate`}
               data-testid="agent-audit-name">
            {data.agent_name || data.agent_id}
          </div>
        </div>
        {data.top_day && (
          <div className="flex items-center gap-2 text-xs bg-amber-50 border border-amber-200 rounded-lg px-2 py-1 text-amber-800"
               data-testid="agent-top-day">
            <Trophy className="w-3.5 h-3.5" />
            <span>
              Best day: <b>{data.top_day.date}</b> · {fmt(data.top_day.converted)} conv
            </span>
          </div>
        )}
      </div>

      {/* Totals row — grid, condensed when compact */}
      <div className={`grid gap-3 ${isFull ? "grid-cols-2 md:grid-cols-4" : "grid-cols-2 md:grid-cols-4"}`}>
        <Stat label="Calls" value={fmt(t.total_calls)} sub={`${t.contactable_pct}% contactable`}
              testId="agent-stat-calls" />
        <Stat label="Total conv" value={fmt(t.total_converted)} sub={`${t.total_conversion_pct}%`}
              testId="agent-stat-total-conv" highlight />
        <Stat label="Call · SMS" value={`${fmt(t.converted)} · ${fmt(t.sms_converted)}`}
              sub={`Call conv ${t.conversion_of_contactable_pct}% of contactable`}
              testId="agent-stat-callsms" />
        <Stat label="WN · Inv" value={`${fmt(t.wn_converted)} · ${fmt(t.invalid_converted)}`}
              sub={`Fresh ${fmt(t.fresh)} · Followup ${fmt(t.followup)}`}
              testId="agent-stat-wninv" />
      </div>

      {/* Sparkline of per-day conversions */}
      {sparkData.length > 1 && (
        <div>
          <div className="text-[11px] text-slate-500 mb-1 flex items-center gap-1">
            <TrendingUp className="w-3 h-3" /> Conversions per day
          </div>
          <div className={isFull ? "h-32" : "h-16"} data-testid="agent-audit-sparkline">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={sparkData} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                <defs>
                  <linearGradient id="agentAuditFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0A84FF" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#0A84FF" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                {isFull && <XAxis dataKey="date" tick={{ fontSize: 10 }} />}
                <Tooltip
                  cursor={{ stroke: "#94a3b8", strokeWidth: 1 }}
                  contentStyle={{ fontSize: 11 }}
                  labelStyle={{ fontSize: 11 }}
                />
                <Area type="monotone" dataKey="converted" stroke="#0A84FF" strokeWidth={2}
                      fill="url(#agentAuditFill)" isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Most-common credit reasons */}
      {data.top_credit_reasons && data.top_credit_reasons.length > 0 && (
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1">
            <Sparkles className="w-3 h-3" /> Common credit reasons
          </div>
          <ul className="space-y-1" data-testid="agent-audit-reasons">
            {data.top_credit_reasons.slice(0, isFull ? 5 : 3).map((r, i) => (
              <li key={i} className="flex items-start justify-between gap-3 text-xs">
                <span className="text-slate-700 min-w-0 truncate" title={r.reason}>{r.reason}</span>
                <span className="text-slate-500 font-mono shrink-0">{fmt(r.count)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, sub, testId, highlight }) {
  return (
    <div className={`p-3 rounded-lg ${highlight ? "bg-[#0A84FF]/5 border border-[#0A84FF]/10" : "bg-slate-50 border border-slate-100"}`}
         data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
      <div className={`font-outfit font-semibold ${highlight ? "text-[#0A84FF]" : "text-slate-900"} text-lg`}>
        {value}
      </div>
      {sub && <div className="text-[10px] text-slate-500 mt-0.5 truncate">{sub}</div>}
    </div>
  );
}
